import { useCallback, useEffect, useRef, useState } from "react";
import {
  AudioEngine,
  type CoachState,
  type TranscriptLine,
  type Usage,
} from "@/lib/audio/AudioEngine";

// React wrapper around AudioEngine. The engine lives in a ref (never recreated
// on render), so React StrictMode's double-invoke can't open two sockets or two
// AudioContexts. start() must be called from a user-gesture handler.
export function useCoachSession(sessionId: string) {
  const engineRef = useRef<AudioEngine | null>(null);
  const [state, setState] = useState<CoachState>("idle");
  const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [error, setError] = useState<string | null>(null);

  const start = useCallback(async () => {
    if (engineRef.current) return; // already running
    const engine = new AudioEngine(sessionId, {
      onState: setState,
      onTranscript: (line) => setTranscript((t) => [...t, line]),
      onUsage: setUsage,
      onError: setError,
    });
    engineRef.current = engine;
    await engine.start();
  }, [sessionId]);

  const pause = useCallback(() => engineRef.current?.pause(), []);
  const resume = useCallback(() => engineRef.current?.resume(), []);
  const nextQuestion = useCallback(() => engineRef.current?.nextQuestion(), []);
  const end = useCallback(() => engineRef.current?.end(), []);

  useEffect(() => {
    return () => {
      engineRef.current?.dispose();
      engineRef.current = null;
    };
  }, []);

  return { state, transcript, usage, error, start, pause, resume, nextQuestion, end };
}
